import { useState } from 'react';
import { Icon } from '@/deploy/components/ui/Icon';
import { Button } from '@/deploy/components/ui/Button';
import { Modal } from '@/deploy/components/ui/Modal';
import { apiServicesService, type ApiCopyConfigResult, type ApiService } from '@/deploy/services/api-services.service';
import type { ApiEnvId } from '@/deploy/services/api-pterodactyl-config.service';
import { apiGroupLabel, canonicalApiEnv } from '@/deploy/utils/apiEnvs';
import {
  DB_SUFFIX_PATTERN, DEFAULT_INSTANCE, apiInstances, findInstance, hasServer, instanceDisplayName,
  instanceErrorMessage, instanceLabel, isDefaultInstance,
} from '@/deploy/utils/apiInstances';
import { CopyConfigFields } from './ApiInstanceModal';
import pageStyles from '@/deploy/pages/ApiServices.module.scss';

// ============================================================
// Copie de appsettings.json d'une instance vers une autre, sur l'env courant. Le contenu ne
// transite jamais par le panel : seuls les champs suffixés reviennent.
// ============================================================

export function ApiCopyConfigModal({ projectId, api, instanceKey, envId, onClose }: {
  projectId: string;
  api: ApiService;
  /** Instance cible, dont le fichier est remplacé. */
  instanceKey: string;
  envId: ApiEnvId;
  onClose: () => void;
}) {
  const env = canonicalApiEnv(envId);
  const envLabel = apiGroupLabel(env);
  const target = findInstance(api, instanceKey);
  // La Principale ne reçoit jamais de copie (elle pointerait sur la base d'une autre instance) : aucune source
  const principalTarget = instanceKey === DEFAULT_INSTANCE;
  const sources = principalTarget ? [] : apiInstances(api).filter((i) => i.key !== instanceKey && hasServer(i, env));
  const [source, setSource] = useState(
    sources.some(isDefaultInstance) ? DEFAULT_INSTANCE : sources[0]?.key ?? '');
  const [suffix, setSuffix] = useState(instanceKey === DEFAULT_INSTANCE ? '' : `_${instanceKey}`);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ApiCopyConfigResult | null>(null);
  if (!target) return null;

  const label = instanceLabel(target);
  const suffixError = suffix.trim() && !DB_SUFFIX_PATTERN.test(suffix.trim())
    ? 'Lettres, chiffres, _ et -, 32 caractères au plus' : null;

  const submit = async () => {
    if (!confirm(`appsettings.json de ${label} sera remplacé sur ${envLabel} : redémarre ensuite l'instance. Continuer ?`)) return;
    setError(null);
    setResult(null);
    setSubmitting(true);
    try {
      setResult(await apiServicesService.copyInstanceConfig(projectId, api.id, instanceKey, {
        environment: env,
        fromInstance: source === DEFAULT_INSTANCE ? undefined : source,
        databaseSuffix: suffix.trim() || undefined,
      }));
    } catch (e) {
      setError(instanceErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open onClose={onClose}
      title={`Copier la config vers ${instanceDisplayName(api, target)}`}
      subtitle={`appsettings.json sur ${envLabel}`}
      width="md"
      footer={<>
        <Button variant="ghost" onClick={onClose} disabled={submitting}>Fermer</Button>
        {!result?.ok && (
          <Button variant="primary" onClick={() => void submit()} loading={submitting}
            disabled={submitting || !source || !!suffixError}>
            Copier la config
          </Button>
        )}
      </>}
    >
      <div className={pageStyles.form}>
        {error && <div className={pageStyles.error}>{error}</div>}
        {principalTarget ? (
          <div className={pageStyles.hintBox}>
            <Icon name="warn" size={12} /> La Principale ne reçoit pas de copie de config : elle pointerait sur la base
            d'une autre instance. Configure-la avec Fichiers.
          </div>
        ) : sources.length === 0 ? (
          <div className={pageStyles.hintBox}>
            <Icon name="warn" size={12} /> Aucune autre instance de {api.name} n'a de serveur sur {envLabel} : rien à copier.
          </div>
        ) : (
          <CopyConfigFields api={api} envId={env} sources={sources} source={source} onSource={setSource}
            suffix={suffix} onSuffix={setSuffix} suffixError={suffixError} allowBlank={false} />
        )}
        {result && (
          <div className={pageStyles.results}>
            <div className={[pageStyles.resultRow, result.ok ? pageStyles.resultOk : pageStyles.resultKo].join(' ')}>
              <Icon name={result.ok ? 'check' : 'x'} size={12} />
              <span style={{ flex: 1 }}>
                {result.ok
                  ? <>{result.message ?? 'Configuration copiée'}{result.changed.length > 0 && <> · champs suffixés : {result.changed.join(', ')}</>}</>
                  : <>Copie KO : {result.error ?? 'erreur inconnue'}</>}
              </span>
            </div>
            {result.warning && (
              <div className={[pageStyles.resultRow, pageStyles.resultWarn].join(' ')}>
                <Icon name="warn" size={12} />
                <span style={{ flex: 1 }}>{result.warning}</span>
              </div>
            )}
            {result.ok && (
              <div className={pageStyles.fieldHint}>Redémarre {label} pour qu'elle relise sa configuration.</div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
